<script>
import Layout from '@layouts/default'
import { vhrMethods } from '@state/helpers'
import { required } from 'vuelidate/lib/validators'
import { vinValidator } from '@utils/validators'
import formatDate from '@utils/format-date'
import RecaptchaAttribution from '@components/recaptcha-attribution'

export default {
  page: {
    title: 'Does My Vehicle Have Any Recall Notices? | AutoCheck.com',
    meta: [{ name: 'description', content: 'Recall Check' }],
  },
  components: { Layout, RecaptchaAttribution },
  data() {
    return {
      vin: '',
      tryingVinSearch: false,
      wasVinValidated: false,
      vinError: false,
      searchSuccess: false,
      vinSummary: {},
      vinMax: 17,
    }
  },
  computed: {
    placeholders() {
      return {
        vin: 'Vehicle Identification Number',
      }
    },
  },
  validations: {
    vin: {
      required,
      vinValidator,
    },
  },
  methods: {
    ...vhrMethods,
    vinSearch() {
      this.$v.$touch()
      if (this.$v.vin.$invalid) {
        this.wasVinValidated = true
      } else {
        this.tryingVinSearch = true
        return this.getVinRecallSummary(this.vin)
          .then((response) => {
            this.tryingVinSearch = false
            this.searchSuccess = true
            this.vinSummary = response
          })
          .catch((error) => {
            this.tryingVinSearch = false
            this.vinError = error
          })
      }
    },
    searchAgain() {
      this.searchSuccess = false
      this.vin = ''
      this.vinError = false
      this.vinSummary = {}
    },
    carMakeUrl(make) {
      const makeName = make.replace(/\s+/g, '').toLowerCase()
      return `http://www.${makeName}.com`
    },
    formattedDate(date, dateFormat) {
      return formatDate(date, dateFormat)
    },
  },
}
</script>

<template>
  <Layout>
    <div class="row mt-4 mb-4">
      <div v-if="!searchSuccess" class="col-md-9 col-12 ">
        <div v-if="!vinError">
          <h1>Does my vehicle have any recall notices?</h1>
          <hr />
          <h3>Start your free vehicle recall search here.</h3>
          <p
            >Did you know? According to the National Highway Traffic Safety
            Administration (NHTSA),
            <a
              href="http://www.usatoday.com/story/money/cars/2016/01/21/nhtsa-recall-completion-washington-auto-show-mark-rosekind/79111364/"
              target="_blank"
              >automakers recalled a record 51.2M vehicles over 868 separate
              recalls in 2015.*</a
            >
            &nbsp;AutoCheck is providing a free service for consumers to check
            for recall notices by vehicle year, make and model. &nbsp;</p
          >
          <p style="font-size:80%">Source: USA Today - Published on 1/21/16</p>

          <p><strong>How does it work?</strong></p>

          <p
            >1. AutoCheck will first search our databases for all VIN-specific
            recall information reported to us by the auto manufacturers.</p
          >
          <p
            ><em
              >2. When data&nbsp;is available in the AutoCheck database based on
              the provided VIN number, AutoCheck will return any open recall
              notices - recalls that are specific to that individual
              vehicsle.</em
            ></p
          >
          <p
            ><em
              >3. When AutoCheck does not have recall information available at a
              VIN-specific (individual vehicle) level, we will share the VIN
              number you entered with our data partner&nbsp;<a
                href="https://www.edmunds.com/?id=apis"
                target="_blank"
                >Edmunds.com</a
              >. &nbsp;They will check if there are recall notices issued for
              the year, make and model of your vehicle, based on the VIN number
              provided.&nbsp;</em
            ></p
          >
          <p>&nbsp;</p>
          <p><strong>Regarding AutoCheck Data Results</strong></p>
          <p
            ><i
              ><strong
                >AutoCheck makes every effort to gather as much recall data as
                possible. However, in some cases, we may not have recall
                information for your vehicle. If you suspect that a vehicle may
                have a recall notice, you should contact your dealership or
                manufacturer immediately.</strong
              ></i
            ></p
          >
          <p>&nbsp;</p>
          <div v-if="!vinError">
            <p><strong>Here’s how to get started:</strong></p>
            <p
              >Enter the&nbsp;<BaseLink :to="{ name: 'vin-basics' }"
                >Vehicle Identification Number (VIN)</BaseLink
              >&nbsp;to begin your search.</p
            >
            <form
              :class="{ 'was-validated ': wasVinValidated }"
              class="needs-validation"
              @submit.prevent="vinSearch"
            >
              <div class="row">
                <div class="col-sm-8 col-12">
                  <BaseInput
                    v-model="$v.vin.$model"
                    name="vin"
                    class="d-flex flood-vin-input"
                    :max="vinMax"
                    :placeholder="placeholders.vin"
                    :has-error="$v.vin.$error && wasVinValidated"
                  >
                    <div v-if="!$v.vin.required" class="invalid-feedback"
                      >Please enter a vehicle identification number to
                      continue</div
                    >
                    <div
                      v-if="!$v.vin.vinValidator && $v.vin.required"
                      class="invalid-feedback"
                    >
                      A valid vehicle identification number is required. Is it a
                      valid VIN? Valid vehicle identification numbers:
                      <ul class="mb-0">
                        <li>Have exactly 17 characters</li>
                        <li>Come from a vehicle manufactured since 1981</li>
                        <li>Do not contain letters I, O, or Q</li>
                      </ul>
                    </div>
                  </BaseInput>
                </div>
                <div class="col-sm-4 col-12">
                  <BaseButton
                    :disabled="tryingVinSearch"
                    class="w-100"
                    type="submit"
                    button-type="buttonPrimary"
                  >
                    <BaseIcon v-if="tryingVinSearch" name="sync" spin />
                    <span v-else>Check VIN</span>
                  </BaseButton>
                </div>
                <div class="col-12 mt-2">
                  <RecaptchaAttribution />
                </div>
              </div>
            </form>
          </div>
        </div>
        <div v-else class="content"
          ><h1>VIN-Specific Recall Search Results</h1>
          <h3
            >AutoCheck checked our database for recalls based on the VIN number
            you provided.</h3
          >
          <p>&nbsp;</p>
          <p><strong>What do the results mean?</strong></p>
          <p
            >1. AutoCheck will indicate if the VIN search was successful
            (indicating that a valid VIN was provided).</p
          >
          <p
            >2. If the search is not successful,
            <router-link to="vin-basics"
              >you may have mistyped or have an incorrect VIN</router-link
            >.&nbsp;</p
          >
          <p
            >3. If the search is successful, any open recall notices pertaining
            to the specific vehicle are listed below. Notices will include the
            date, type, description, and recall code provided by the
            manufacturer. If open recall notices are indicated, we suggest you
            contact your local dealer to schedule necessary repairs.</p
          >
          <p>&nbsp;</p>
          <p><strong>Regarding AutoCheck Data Results</strong></p>
          <p
            ><i
              ><strong
                >AutoCheck makes every effort to gather as much recall data as
                possible. However, in some cases, we may not have recall
                information for your vehicle. If you suspect that a vehicle may
                have a recall notice, you should contact your dealership or
                manufacturer immediately.</strong
              ></i
            ></p
          >
          <p>&nbsp;</p>
          <hr
        /></div>

        <div v-if="vinError">
          <hr />
          <div class="recall-data-box">
            <p
              >We did NOT find a search result for VIN Number:
              <b style="text-transform: uppercase;">{{ vin }}</b
              >.</p
            >
            <br />
            <p
              >The VIN can be found by looking at the dashboard on the driver's
              side of the vehicle. The easiest way to view it is to stand
              outside the vehicle on the driver's side and look at the corner of
              the dashboard where it meets the windshield. If the VIN cannot be
              found there, open the driver's side door and look at the door post
              (where the door latches when it is closed). It is likely that the
              VIN will also be displayed in this location.
            </p>

            <a class="linkStyle" href="#" @click="searchAgain">Search Again</a>
          </div>
        </div>
      </div>
      <div v-if="searchSuccess" class="col-md-9 col-12 ">
        <div v-if="vinSummary.recalSummarySource !== 'EDMUNDS'">
          <h1>VIN-Specific Recall Search Results</h1>
          <hr />
          <h3
            >AutoCheck checked our database for recalls based on the VIN number
            you provided.</h3
          >
          <p>&nbsp;</p>
          <p><strong>What do the results mean?</strong></p>
          <p
            >1. AutoCheck will indicate if the VIN search was successful
            (indicating that a valid VIN was provided).</p
          >
          <p
            >2. If the search is not successful,
            <BaseLink :to="{ name: 'vin-basics' }"
              >you may have mistyped or have an incorrect VIN</BaseLink
            >.&nbsp;</p
          >
          <p
            >3. If the search is successful, any open recall notices pertaining
            to the specific vehicle are listed below. Notices will include the
            date, type, description, and recall code provided by the
            manufacturer. If open recall notices are indicated, we suggest you
            contact your local dealer to schedule necessary repairs.</p
          >
        </div>
        <div v-else>
          <h1>Vehicle Year, Make and Model Recall Search Results</h1>
          <hr />
          <h4
            >AutoCheck did not return a result for that specific VIN number.
          </h4>
          <p>&nbsp;</p>
          <p><strong>What do the results mean?</strong></p>
          <p
            >The specific vehicle VIN you provided may or may not be impacted by
            a recall. Through a partnership with
            <a href="https://www.edmunds.com/?id=apis" target="_blank"
              >Edmunds.com</a
            >, we shared the VIN number you provided with them in order to
            obtain generalized recall information that may be available - based
            on the year, make and model of the vehicle that corresponds to the
            VIN number you provided. Below are the search results reported by
            <a href="https://www.edmunds.com/?id=apis" target="_blank"
              >Edmunds.com</a
            >.</p
          >
        </div>

        <p>&nbsp;</p>
        <p><strong>Regarding AutoCheck Data Results</strong></p>
        <p
          ><i
            ><strong
              >AutoCheck makes every effort to gather as much recall data as
              possible. However, in some cases, we may not have recall
              information for your vehicle. If you suspect that a vehicle may
              have a recall notice, you should contact your dealership or
              manufacturer immediately.</strong
            ></i
          ></p
        >
        <p>&nbsp;</p>
        <hr />
        <div class="recall-data-box">
          <p v-if="vinSummary.recalSummarySource !== 'EDMUNDS'"
            ><b
              >We have completed a successful search for VIN Number:
              <span style="text-transform: uppercase;">{{ vin }}</span
              >.</b
            ></p
          ><p> </p>

          <p
            ><b
              >This VIN appears to be a {{ vinSummary.modelYear }}
              {{ vinSummary.make }} {{ vinSummary.model }},
              {{ vinSummary.style }}</b
            ></p
          >
          <div v-if="vinSummary.recallCount === 0" class="recallheadline">
            <h3
              ><b
                >Good News!! We did not find any recall notices on this
                vehicle.</b
              ></h3
            >
          </div>
          <div v-else>
            <div
              v-if="vinSummary.recalSummarySource !== 'EDMUNDS' && !vinError"
              class="recallheadline"
            >
              <p
                ><b>There are {{ vinSummary.recallCount }} recall notice(s)</b>
                for this {{ vinSummary.modelYear }} {{ vinSummary.make }}
                {{ vinSummary.model }} vehicle. Locate your nearest authorized
                <a :href="carMakeUrl(vinSummary.make)">
                  {{ vinSummary.make }} dealership</a
                >
                for additional information or to schedule repairs.</p
              >
            </div>
            <div v-else class="recallheadline">
              <p
                ><b>There are {{ vinSummary.recallCount }} recall notice(s)</b>
                for {{ vinSummary.modelYear }} {{ vinSummary.make }}
                {{ vinSummary.model }} vehicles. The specific VIN provided may
                not be impacted by these results, or repair(s) may have already
                been performed on the specific vehicle searched. Contact
                {{ vinSummary.make }} or a {{ vinSummary.make }} dealership for
                additional information.</p
              >
            </div>
            <div v-if="vinSummary.recalSummarySource !== 'EDMUNDS'">
              <div
                v-for="(notice, index) in vinSummary.recallHolder"
                :key="index"
                class="mt-4 mb-4"
                :class="$style.results"
              >
                <p
                  ><strong>Date of Recall:</strong>
                  {{
                    notice.manufacturedTo
                      ? formattedDate(notice.manufacturedTo, 'MM/DD/YYYY')
                      : 'UNAVAILABLE'
                  }}</p
                >
                <p
                  ><strong>Recall Type:</strong>
                  {{
                    notice.influencedBy === 'MFR' || 'MANUFACTURER RECALL'
                      ? 'MANUFACTURER RECALL'
                      : 'OTHER RECALL'
                  }}</p
                >
                <p
                  ><strong>Recall Description:</strong>
                  {{ notice.componentDescription }}</p
                >
                <p
                  ><strong>Recall Code:</strong>
                  {{ notice.manufacturerRecallNumber }}</p
                >
              </div>
            </div>
            <div v-else>
              <div
                v-for="(notice, index) in vinSummary.recallHolder"
                :key="index"
                class="mt-4 mb-4"
                :class="$style.results"
              >
                <p><b>Date of Recall:</b></p>
                <p><b>Recall Description:</b> {{ notice.defectDescription }}</p>
                <p><b>Recall Consequence:</b> {{ notice.defectConsequence }}</p>
                <p
                  ><b>Corrective Action:</b>
                  {{ notice.defectCorrectiveAction }}</p
                >
              </div>
            </div>
          </div>
          <br />
          <a class="linkStyle" href="#" @click="searchAgain">Search Again</a>
        </div>
      </div>
      <div class="col-md-3 col-12">
        <div class="content-page-right-navigation"
          ><div :class="$style.sideNav">
            <h4>Learn More</h4>
            <p
              >To learn more about recall information, and to check the latest
              recall data reported to the federal government, AutoCheck suggests
              that you visit
              <a href="http://www.safercar.gov/"
                >The National Highway Traffic Safety Administration's website</a
              >&nbsp;directly.</p
            >
            <p
              ><a href="http://www.safercar.gov/">
                <img
                  alt="National Highway Traffic Safety Administration"
                  src="https://www.autocheck.com/communications/nhtsalogo.jpg"/></a
            ></p>
            <p><strong>Year/Make/Model Recall Data</strong></p>
            <p
              ><a href="http://www.edmunds.com/?id=apis">
                <img
                  src="https://open-api.edmunds.com/api/openapi/v1/logo?size=220&amp;format=horizontal&amp;retina=false&amp;color=white&amp;api_key=7drsmxrjsm5d583agbw4f78m"
                  alt=""
                /> </a
            ></p>
            <p>&nbsp;</p>
            <p class="mb-3"><strong>Manufacturer Recall URLs:</strong></p>
            <p
              ><a
                href="http://owners.acura.com/service-maintenance/recalls"
                target="_blank"
                >Acura</a
              >&nbsp;|&nbsp;<a
                href="http://web.audiusa.com/recall/"
                target="_blank"
                >Audi</a
              >&nbsp;|&nbsp;<a
                href="http://www.bmwusa.com/standard/content/owner/safetyrecalls.aspx?mobileoverride=true"
                target="_blank"
                >BMW</a
              >&nbsp;|&nbsp;<a
                href="https://my.buick.com/recalls"
                target="_blank"
                >Buick</a
              ></p
            >
            <p
              ><a href="https://my.cadillac.com/recalls" target="_blank"
                >Cadillac</a
              >&nbsp;|&nbsp;<a
                href="https://my.chevrolet.com/recalls"
                target="_blank"
                >Chevrolet</a
              >&nbsp;</p
            >
            <p
              ><a
                href="http://www.chrysler.com/webselfservice/chrysler/Recall.html"
                target="_blank"
                >Chrysler</a
              >&nbsp;|&nbsp;<a
                href="http://www.dodge.com/webselfservice/dodge/Recall.html"
                target="_blank"
                >Dodge</a
              >&nbsp;|&nbsp;<a
                href="http://auto.ferrari.com/en_US/owners/car-part-services/"
                target="_blank"
                >Ferrari</a
              ></p
            >
            <p
              ><a
                href="http://www.chrysler.com/webselfservice/chrysler/Recall.html"
                target="_blank"
                >FIAT</a
              >&nbsp;|&nbsp;<a
                href="https://owner.ford.com/tools/account/maintenance/recalls.html"
                target="_blank"
                >Ford</a
              >&nbsp;|&nbsp;<a href="https://my.gm.com/recalls" target="_blank"
                >GMC</a
              ></p
            >
            <p
              ><a
                href="http://recalls.owners.honda.com/service-maintenance/recalls"
                target="_blank"
                >Honda</a
              >&nbsp;|&nbsp;<a href="https://my.gm.com/recalls" target="_blank"
                >Hummer</a
              ></p
            >
            <p
              ><a
                href="https://autoservice.hyundaiusa.com/campaignhome"
                target="_blank"
                >Hyundai</a
              >&nbsp;|&nbsp;<a
                href="http://www.infinitiusa.com/recalls-vin"
                target="_blank"
                >Infiniti</a
              ></p
            >
            <p
              ><a
                href="http://www.jaguarusa.com/owners/vin-recall.html"
                target="_blank"
                >Jaguar</a
              >&nbsp;|&nbsp;<a
                href="http://www.jeep.com/webselfservice/jeep/Recall.html"
                target="_blank"
                >Jeep</a
              >&nbsp;|&nbsp;<a
                href="http://www.kia.com/us/en/content/owners/safety-recall"
                target="_blank"
                >Kia</a
              ></p
            >
            <p
              ><a
                href="http://www.landroverusa.com/ownership/index.html"
                target="_blank"
                >Land Rover</a
              >&nbsp;|&nbsp;<a
                href="http://www.toyota.com/recall"
                target="_blank"
                >Lexus</a
              ></p
            >
            <p
              ><a
                href="https://owner.lincoln.com/tools/account/maintenance/recalls.html"
                target="_blank"
                >Lincoln</a
              >&nbsp;|&nbsp;<a
                href="https://www.mazdausa.com/owners/recalls"
                target="_blank"
                >Mazda</a
              ></p
            >
            <p
              ><a href="https://www.mbusa.com/mercedes/recall" target="_blank"
                >Mercedes-Benz</a
              ></p
            >
            <p
              ><a
                href="https://owner.lincoln.com/tools/account/maintenance/recalls.html"
                target="_blank"
                >Mercury</a
              >&nbsp;|&nbsp;<a
                href="http://www.miniusa.com/content/miniusa/en/owners/tools-support/recall.html"
                target="_blank"
                >MINI</a
              ></p
            >
            <p
              ><a href="http://www.mitsubishicars.com/recall" target="_blank"
                >Mitsubishi</a
              >&nbsp;|&nbsp;<a
                href="http://www.nissanusa.com/recalls-vin#/"
                target="_blank"
                >Nissan</a
              ></p
            >
            <p
              ><a href="https://my.gm.com/recalls" target="_blank">Oldsmobile</a
              >&nbsp;</p
            >
            <p
              ><a href="https://my.gm.com/recalls" target="_blank">Pontiac</a
              >&nbsp;|&nbsp;<a
                href="https://recall.porsche.com/prod/pag/vinrecalllookup.nsf/VIN?ReadForm"
                target="_blank"
                >Porsche</a
              ></p
            >
            <p
              ><a
                href="http://www.ramtrucks.com/webselfservice/ram/Recall.html"
                target="_blank"
                >RAM</a
              >&nbsp;|&nbsp;<a href="https://my.gm.com/recalls" target="_blank"
                >Saab</a
              ></p
            >
            <p
              ><a href="https://my.gm.com/recalls" target="_blank">Saturn</a
              >&nbsp;| &nbsp;<a
                href="http://www.toyota.com/recall"
                target="_blank"
                >Scion</a
              ></p
            >
            <p
              ><a
                href="http://www.subaru.com/vehicle-recalls.html"
                target="_blank"
                >Subaru</a
              >&nbsp;|&nbsp;<a
                href="https://www.tesla.com/support/annual-and-recall-service"
                target="_blank"
                >Tesla</a
              ></p
            >
            <p
              ><a href="http://www.toyota.com/recall" target="_blank">Toyota</a
              >&nbsp;|&nbsp;<a
                href="http://www.vw.com/owners-recalls/"
                target="_blank"
                >Volkswagen</a
              ></p
            >
            <p
              ><a
                href="http://www.volvocars.com/us/own/explore/recall-information"
                target="_blank"
                >Volvo</a
              ></p
            ></div
          ></div
        >
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.sideNav {
  p {
    margin-bottom: 5px;
  }
}
.results {
  font-size: 14px;
  p {
    margin-bottom: 5px;
  }
}
</style>
